/* eslint-disable jsx-a11y/no-autofocus */
import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faSliders,
} from '@fortawesome/sharp-solid-svg-icons';
import { centroid } from '@turf/turf';
import { uniqBy } from 'lodash';
import { LngLatLike } from 'maplibre-gl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import LoadingIcon from '@/components/loadingIcon';
import OutsideClickWrapper from '@/components/OutsideClickWrapper';
import { tailwindTypographyClassNameMapping } from '@/components/typography';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import {
  ADV_SEARCH_RESULTS_URL,
  ADVANCED_SEARCH_URL,
  INSIGHTS_CATALOG_LIBRARY_URL,
  SAVED_LAND_PLAT_MAP_URL,
} from '@/constants/urls';
import { County } from '@/endpoints/itasca/county/getCounties';
import { State } from '@/endpoints/itasca/state/getState';
import useDebounce from '@/hooks/useDebounce';
import useWebStorage from '@/hooks/useWebStorage';
import { ZOOM_LEVELS } from '@/mapbox-layers';
import type { IPlaceFeature } from '@/models/search';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { addLocationPin } from '@/utils/searchPins';
import zoomToBounds from '@/utils/zoomToBounds';

import { Tools } from '../toolbar';
import { CountyDropdown } from './countyDropdown';
import { IndexedSearchResult, SearchResult, SearchResultType } from './models';
import { SearchElement } from './searchElements';
import { useSearchResults } from './searchHooks/useSearchResults';
import { SearchPreviousResults } from './searchPreviousResults';
import { SearchResultsSection } from './searchResultsSection';
import { SearchResultsSubSection } from './searchResultsSubSection';
import { StateDropdown } from './stateDropdown';

export const PreviousSearchResultsStorageKey = 'previous-search-results';

type Props = {
  showSearch: boolean;
  showTools: boolean;
};

/**
 *  This is the entire on-map search that floats in the top left of the map and searches the app
 */
export const OnMapSearch: React.FC<Props> = ({ showSearch, showTools }) => {
  const router = useRouter();
  const { mapInstance: map } = useMapboxContext();
  const [cursor, setCursor] = useState(0);
  const [input, setInput] = useState('');
  const [searchHasFocus, setSearchHasFocus] = useState(false);
  const [
    searchHasBeenManuallyClickedInto,
    setSearchHasBeenManuallyClickedInto,
  ] = useState(false); // Mostly matches focus state execpt defaults false until clicked into
  const { track } = useAnalytics();

  const {
    updateValue: updatePreviousSearchResults,
    savedValue: previousSearchResults,
  } = useWebStorage({
    key: PreviousSearchResultsStorageKey,
  });

  const [selectedCounty, setSelectedCounty] =
    useState<IndexedSearchResult<County> | null>(null);
  const [selectedState, setSelectedState] =
    useState<IndexedSearchResult<State> | null>(null);

  const { debouncedValue: debouncedInput } = useDebounce(input, 500);

  const ref = useRef<HTMLDivElement>(null);

  const {
    searchResults,
    lonLatResults,
    savesLoading,
    layersLoading,
    stateLoading,
    countyLoading,
    placesLoading,
    townshipLoading,
    parcelNumberLoading,
    parcelOwnersLoading,
    parcelOwnerAddressLoading,
    sectionTownshipRangeLoading,
    placesResults,
    countyResults,
    stateResults,
    townshipResults,
    parcelNumberResults,
    parcelNumbersSeeMore,
    layersResults,
    parcelOwnersResults,
    parcelOwnersSeeMore,
    parcelOwnerAddressesResults,
    parcelOwnerAddressesSeeMore,
    layersSeeMore,
    savesResults,
    savesSeeMore,
    sectionTownshipRangeResults,
    areAnyResultsLoading,
  } = useSearchResults(debouncedInput);

  const clickCurrent = useCallback(() => {
    // Click the result
    (
      ref.current?.querySelector(`[tabindex='${cursor}']`) as HTMLDivElement
    )?.click();
  }, [cursor]);

  const clearInput = useCallback(() => {
    setCursor(0);
    setInput('');
  }, []);

  const showPreviousResults = useMemo(() => {
    if (!searchHasBeenManuallyClickedInto) {
      return false;
    }

    if (selectedCounty || selectedState) {
      return false;
    }

    // Only if there is no input, should we show previous results
    return !debouncedInput;
  }, [
    debouncedInput,
    searchHasBeenManuallyClickedInto,
    selectedCounty,
    selectedState,
  ]);

  const showDropdown = useMemo(() => {
    if (!searchHasFocus) {
      return false;
    }

    if (selectedCounty || selectedState) {
      return true;
    }

    if (!debouncedInput) {
      return false;
    }

    return !!searchResults.length || areAnyResultsLoading;
  }, [
    areAnyResultsLoading,
    debouncedInput,
    searchHasFocus,
    searchResults.length,
    selectedCounty,
    selectedState,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowUp' && cursor > 0) {
        setCursor((prev) => prev - 1);
      } else if (e.key === 'ArrowDown' && cursor < searchResults.length - 1) {
        setCursor((prev) => prev + 1);
      } else if (e.key === 'Enter') {
        clickCurrent();
      }
    },
    [clickCurrent, cursor, searchResults.length]
  );

  const handlePlaceResultClick = useCallback(
    (result: IndexedSearchResult<IPlaceFeature>) => {
      if (!map) return;

      zoomToBounds(map, result.geometry);

      if (result.geometry.type === 'Point') {
        addLocationPin(map, {
          center: result.geometry.coordinates as [number, number],
          context: undefined,
          geometry: result.geometry,
          place_name: '',
          place_type: [],
          properties: null,
          relevance: 0,
          text: '',
          type: 'Feature',
        });
      }

      clearInput();
    },
    [clearInput, map]
  );

  const saveResultToStorage = useCallback(
    (result) => {
      const prev: SearchResult<any>[] = (
        previousSearchResults as SearchResult<any>[]
      )?.length
        ? (previousSearchResults as SearchResult<any>[])
        : [];

      // Only save up to 10 unique searches
      const newSearchResults = uniqBy([result, ...prev], 'id').splice(0, 10);
      updatePreviousSearchResults(newSearchResults);
    },
    [previousSearchResults, updatePreviousSearchResults]
  );

  const trackResultClicked = useCallback(
    (result: SearchResult<any>, hardcoded_link?: string) => {
      track({
        data: {
          hardcoded_link,
          item_label: result.label,
          original: result.original,
          search_term: input,
          selected_county: selectedCounty,
          selected_state: selectedState,
          type: result.type,
        },
        event: TrackingEvent.SEARCH_BAR_RESULT_CLICKED,
      });
    },
    [input, track, selectedCounty, selectedState]
  );

  const onResultsClickMap: Record<SearchResultType, (result) => void> =
    useMemo(() => {
      return {
        [SearchResultType.GO_TO_POINT]: (result) => {
          trackResultClicked(result);
          if (!map) return;
          map.flyTo({
            center: result.geometry.coordinates as LngLatLike,
            zoom: 17,
          });

          addLocationPin(map, {
            center: result.geometry.coordinates as [number, number],
            context: undefined,
            geometry: result.geometry,
            place_name: '',
            place_type: [],
            properties: null,
            relevance: 0,
            text: '',
            type: 'Feature',
          });
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
        [SearchResultType.MAPBOX_PLACE]: (result) => {
          trackResultClicked(result);
          handlePlaceResultClick(result);
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
        [SearchResultType.LAYER_LIBRARY]: (result) => {
          trackResultClicked(result);
          if (!map) return;
          router.push(result.url);
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
        [SearchResultType.SAVED_MAP]: (result) => {
          trackResultClicked(result);
          if (!map) return;
          router.push(result.url);
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
        [SearchResultType.COUNTY]: (result) => {
          trackResultClicked(result);
          setSelectedCounty(result);
          saveResultToStorage(result);
        },
        [SearchResultType.PARCEL_OWNER_NAME]: (result) => {
          trackResultClicked(result);
          router.push(result.url);
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
        [SearchResultType.PARCEL_OWNER_ADDRESS]: (result) => {
          trackResultClicked(result);
          router.push(result.url);
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
        [SearchResultType.PARCEL_NUMBER]: (result) => {
          trackResultClicked(result);
          if (map) {
            zoomToBounds(map, result.geometry, false, 100);
          }
          router.push(result.url);
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
        [SearchResultType.STATE]: (result) => {
          trackResultClicked(result);
          setSelectedState(result);
          saveResultToStorage(result);
        },
        [SearchResultType.TOWNSHIP_RANGE]: (result) => {
          trackResultClicked(result);
          if (!map) return;
          zoomToBounds(map, result.geometry, false, 100);
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
        [SearchResultType.SECTION_TOWNSHIP_RANGE]: (result) => {
          trackResultClicked(result);
          if (!map) return;
          map.flyTo({
            center: centroid(result.geometry).geometry.coordinates as [
              number,
              number
            ],
            zoom: ZOOM_LEVELS.SECTIONS.MAX - 0.5,
          });
          saveResultToStorage(result);
          setSearchHasFocus(false);
          setSearchHasBeenManuallyClickedInto(false);
        },
      };
    }, [
      handlePlaceResultClick,
      map,
      router,
      saveResultToStorage,
      trackResultClicked,
    ]);

  if (!showTools && !showSearch) {
    return null;
  }

  return (
    <OutsideClickWrapper
      onOutsideClick={() => {
        setSearchHasFocus(false);
        setSearchHasBeenManuallyClickedInto(false);
      }}
    >
      <div className="flex h-full max-h-full gap-x-4 pb-6 pl-6 pt-6">
        {showSearch && (
          <div className="flex w-[300px] flex-col gap-y-1">
            <div
              className={twMerge(
                'group pointer-events-auto flex h-10 items-center rounded-full bg-white px-4 py-1',
                'focus-within:outline-solid focus-within:outline focus-within:outline-4  focus-within:outline-blue-utility'
              )}
              role="button" // TODO: this isnt a button
              tabIndex={0}
              onKeyDown={handleKeyDown}
            >
              {!selectedCounty && !selectedState && (
                // Search input
                <input
                  autoFocus
                  className={twMerge(
                    'min-w-[200px] font-[Roboto] focus:outline-none focus:ring-0',
                    tailwindTypographyClassNameMapping.body1
                  )}
                  placeholder="What are you looking for?"
                  type="text"
                  value={input}
                  onChange={(e) => {
                    if (e.target.value === input) {
                      return;
                    }

                    setInput(e.target.value);

                    // Take focus if the search bar hasn't been clicked into but user starts typing because of `autoFocus`
                    if (!searchHasFocus) {
                      setSearchHasFocus(true);
                    }
                  }}
                  onClick={() => {
                    setSearchHasFocus(true);
                    setSearchHasBeenManuallyClickedInto(true);
                  }}
                />
              )}
              {/* If there is a selected county / state, then show this input and clicking clears it */}
              {(selectedCounty || selectedState) && (
                <input
                  className={twMerge(
                    'min-w-[200px] font-[Roboto] focus:outline-none focus:ring-0',
                    tailwindTypographyClassNameMapping.body1
                  )}
                  type="text"
                  value={selectedCounty?.label ?? selectedState?.label}
                  onChange={(e) => setInput(e.target.value)}
                  onClick={() => {
                    setSearchHasFocus(true);
                    setSelectedCounty(null);
                    setSelectedState(null);
                  }}
                />
              )}
              <div className="flex h-full items-center gap-x-1">
                {areAnyResultsLoading ? (
                  <div
                    className="flex h-8 w-8 items-center justify-center px-2 py-[7px]"
                    data-tooltip-content="Searching..."
                    data-tooltip-delay-show={300}
                    data-tooltip-id={GLOBAL_TOOLTIP}
                    data-tooltip-place="bottom"
                  >
                    <LoadingIcon
                      className="max-h-[16px] max-w-[16px] text-green-50"
                      size="sm"
                    />
                  </div>
                ) : (
                  <IconButton
                    data-tooltip-content="Search"
                    data-tooltip-delay-show={1000}
                    data-tooltip-id={GLOBAL_TOOLTIP}
                    data-tooltip-place="bottom"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.GHOST}
                    onClick={clickCurrent}
                  >
                    <FontAwesomeIcon
                      className="text-gray-70 group-focus-within:text-green-50"
                      icon={faMagnifyingGlass}
                    />
                  </IconButton>
                )}
                <div className="flex h-full w-[1px] bg-gray-20" />
                <Link className="flex items-center" href={ADVANCED_SEARCH_URL}>
                  <IconButton
                    data-tooltip-content="Advanced Search"
                    data-tooltip-delay-show={1000}
                    data-tooltip-id={GLOBAL_TOOLTIP}
                    data-tooltip-place="bottom"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.GHOST}
                  >
                    <FontAwesomeIcon
                      className="text-gray-70"
                      icon={faSliders}
                      size="lg"
                    />
                  </IconButton>
                </Link>
              </div>
            </div>

            {showPreviousResults && (
              <SearchPreviousResults onResultsClickMap={onResultsClickMap} />
            )}

            {/* Drop down results */}
            {showDropdown && (
              <div
                ref={ref}
                className="pointer-events-auto flex shrink flex-col overflow-auto rounded-md bg-gray-10 py-1"
                role="button"
                tabIndex={0}
                onKeyDown={handleKeyDown}
              >
                {/* County Results */}
                {selectedCounty && (
                  <CountyDropdown
                    cursor={cursor}
                    selectedCounty={selectedCounty}
                    setCursor={setCursor}
                    trackResultClicked={trackResultClicked}
                  />
                )}
                {selectedState && (
                  <StateDropdown
                    cursor={cursor}
                    selectedState={selectedState}
                    setCursor={setCursor}
                    trackResultClicked={trackResultClicked}
                  />
                )}

                {/* Search Results */}
                {!selectedCounty && !selectedState && (
                  <div className="flex flex-col divide-y-1 divide-solid divide-gray-20">
                    {/* County section */}
                    {!!countyResults.length && (
                      <SearchResultsSection
                        loading={countyLoading}
                        title="Counties"
                      >
                        {countyResults?.map((x) => {
                          const { index } = x;
                          return (
                            <SearchElement
                              key={x.id}
                              focus={cursor === index}
                              index={index}
                              value={x.label}
                              onArrowClick={() => {
                                onResultsClickMap[SearchResultType.COUNTY](x);
                              }}
                              onClick={() => {
                                onResultsClickMap[SearchResultType.COUNTY](x);
                              }}
                              onMouseEnter={() => {
                                setCursor(index);
                              }}
                            />
                          );
                        })}
                      </SearchResultsSection>
                    )}

                    {/* State section */}
                    {!!stateResults.length && (
                      <SearchResultsSection
                        loading={stateLoading}
                        title="States"
                      >
                        {stateResults?.map((x) => {
                          const { index } = x;
                          return (
                            <SearchElement
                              key={x.id}
                              focus={cursor === index}
                              index={index}
                              value={x.label}
                              onArrowClick={() => {
                                onResultsClickMap[SearchResultType.STATE](x);
                              }}
                              onClick={() => {
                                onResultsClickMap[SearchResultType.STATE](x);
                              }}
                              onMouseEnter={() => {
                                setCursor(index);
                              }}
                            />
                          );
                        })}
                      </SearchResultsSection>
                    )}

                    {/* "Go to" results in the drop down */}
                    <SearchResultsSection
                      loading={
                        placesLoading ||
                        townshipLoading ||
                        parcelNumberLoading ||
                        sectionTownshipRangeLoading
                      }
                      noResults={
                        !placesResults.length &&
                        !townshipResults.length &&
                        !parcelNumberResults.length &&
                        !lonLatResults.length &&
                        !sectionTownshipRangeResults.length
                      }
                      title="Go To"
                    >
                      {!!lonLatResults.length && (
                        <SearchResultsSubSection title="Latitude, Longitude">
                          <SearchElement
                            key={lonLatResults[0].id}
                            focus={cursor === lonLatResults[0].index}
                            index={lonLatResults[0].index}
                            value={lonLatResults[0].label}
                            onClick={() => {
                              onResultsClickMap[SearchResultType.GO_TO_POINT](
                                lonLatResults[0]
                              );
                            }}
                            onMouseEnter={() => {
                              setCursor(lonLatResults[0].index);
                            }}
                          />
                        </SearchResultsSubSection>
                      )}
                      {!!townshipResults.length && (
                        <SearchResultsSubSection title="Townships">
                          {/* Townships under the GoTo Section */}
                          {townshipResults?.map((x) => {
                            const { index } = x;
                            return (
                              <SearchElement
                                key={x.id}
                                focus={cursor === index}
                                index={index}
                                value={x.label}
                                onClick={() => {
                                  onResultsClickMap[
                                    SearchResultType.TOWNSHIP_RANGE
                                  ](x);
                                }}
                                onMouseEnter={() => {
                                  setCursor(index);
                                }}
                              />
                            );
                          })}
                        </SearchResultsSubSection>
                      )}
                      {!!sectionTownshipRangeResults.length && (
                        <SearchResultsSubSection title="Section Township Ranges">
                          {/* Section township ranges under the GoTo Section */}
                          {sectionTownshipRangeResults?.map((x) => {
                            const { index } = x;
                            return (
                              <SearchElement
                                key={x.id}
                                focus={cursor === index}
                                index={index}
                                value={x.label}
                                onClick={() => {
                                  onResultsClickMap[
                                    SearchResultType.SECTION_TOWNSHIP_RANGE
                                  ](x);
                                }}
                                onMouseEnter={() => {
                                  setCursor(index);
                                }}
                              />
                            );
                          })}
                        </SearchResultsSubSection>
                      )}
                      {!!placesResults.length && (
                        <SearchResultsSubSection title="Places">
                          {placesResults?.map((x) => {
                            const { index } = x;
                            return (
                              <SearchElement
                                key={x.id}
                                focus={cursor === index}
                                index={index}
                                value={x.label}
                                onClick={() =>
                                  onResultsClickMap[
                                    SearchResultType.MAPBOX_PLACE
                                  ](x)
                                }
                                onMouseEnter={() => {
                                  setCursor(index);
                                }}
                              />
                            );
                          })}
                        </SearchResultsSubSection>
                      )}
                      {!!parcelNumberResults.length && (
                        <SearchResultsSubSection
                          seeMoreResults={{
                            count: parcelNumbersSeeMore,
                            url: `${ADV_SEARCH_RESULTS_URL}?search=true&apn=${input}`,
                          }}
                          title="Parcel Numbers"
                        >
                          {parcelNumberResults?.map((x) => {
                            const { index } = x;
                            return (
                              <SearchElement
                                key={x.id}
                                focus={cursor === index}
                                index={index}
                                value={x.node}
                                onClick={() => {
                                  onResultsClickMap[
                                    SearchResultType.PARCEL_NUMBER
                                  ](x);
                                }}
                                onMouseEnter={() => {
                                  setCursor(index);
                                }}
                              />
                            );
                          })}
                        </SearchResultsSubSection>
                      )}
                    </SearchResultsSection>

                    {/* Layers results in the drop down */}
                    <SearchResultsSection
                      loading={layersLoading}
                      noResults={!layersResults.length}
                      seeMoreResults={{
                        count: layersSeeMore,
                        url: `${INSIGHTS_CATALOG_LIBRARY_URL}?search=${input}`,
                      }}
                      title="Layers"
                    >
                      {layersResults?.map((x) => {
                        const { index } = x;
                        return (
                          <SearchElement
                            key={x.id}
                            focus={cursor === index}
                            index={index}
                            value={x.label}
                            onClick={() => {
                              onResultsClickMap[SearchResultType.LAYER_LIBRARY](
                                x
                              );
                            }}
                            onMouseEnter={() => {
                              setCursor(index);
                            }}
                          />
                        );
                      })}
                    </SearchResultsSection>

                    {/* Saved maps results in the drop down */}
                    <SearchResultsSection
                      loading={savesLoading}
                      noResults={!savesResults.length}
                      seeMoreResults={{
                        count: savesSeeMore,
                        url: `${SAVED_LAND_PLAT_MAP_URL}?search=${input}`,
                      }}
                      title="Saved Maps"
                    >
                      {savesResults?.map((x) => {
                        const { index } = x;
                        return (
                          <SearchElement
                            key={x.id}
                            focus={cursor === index}
                            index={index}
                            value={x.label}
                            onClick={() => {
                              onResultsClickMap[SearchResultType.SAVED_MAP](x);
                            }}
                            onMouseEnter={() => {
                              setCursor(index);
                            }}
                          />
                        );
                      })}
                    </SearchResultsSection>

                    {/* Parcel Owners results in the drop down */}
                    <SearchResultsSection
                      loading={parcelOwnersLoading}
                      noResults={!parcelOwnersResults.length}
                      seeMoreResults={{
                        count: parcelOwnersSeeMore,
                        url: `${ADVANCED_SEARCH_URL}?owner_name=${input}`,
                      }}
                      title="Parcel Owner Name"
                    >
                      {parcelOwnersResults?.map((x) => {
                        const { index } = x;
                        return (
                          <SearchElement
                            key={x.id}
                            focus={cursor === index}
                            index={index}
                            value={x.label}
                            onClick={() => {
                              onResultsClickMap[
                                SearchResultType.PARCEL_OWNER_NAME
                              ](x);
                            }}
                            onMouseEnter={() => {
                              setCursor(index);
                            }}
                          />
                        );
                      })}
                    </SearchResultsSection>
                    {/* Parcel Owner Address results in the drop down */}
                    <SearchResultsSection
                      loading={parcelOwnerAddressLoading}
                      noResults={!parcelOwnerAddressesResults.length}
                      seeMoreResults={{
                        count: parcelOwnerAddressesSeeMore,
                        url: `${ADVANCED_SEARCH_URL}?mailing_address=${input}`,
                      }}
                      title="Parcel Owner Address"
                    >
                      {parcelOwnerAddressesResults?.map((x) => {
                        const { index } = x;
                        return (
                          <SearchElement
                            key={x.id}
                            focus={cursor === index}
                            index={index}
                            value={x.label}
                            onClick={() => {
                              onResultsClickMap[
                                SearchResultType.PARCEL_OWNER_ADDRESS
                              ](x);
                            }}
                            onMouseEnter={() => {
                              setCursor(index);
                            }}
                          />
                        );
                      })}
                    </SearchResultsSection>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tools button */}
        {showTools && (
          <div className="flex h-full w-full">
            <Tools />
          </div>
        )}
      </div>
    </OutsideClickWrapper>
  );
};
